import { useState, useEffect } from 'react';
import * as React from 'react';
import {
  FaPhoneAlt, FaStar, FaRegStar, FaEnvelope, FaPhone,
  FaBriefcase, FaLanguage, FaCalendarAlt, FaUser,
  FaMapMarkerAlt, FaIdCard, FaUtensils, FaChevronUp,
  FaFileAlt, FaDownload, FaIceCream,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { colors } from '../../employees/styles/personalakteStyles';
import InterviewService from '../services/InterviewService';
import RecruitingService from '../services/RecruitingService';
import type {
  Applicant, InterviewFormData, InterviewRecommendation,
  PhoneInterviewSubmitData, PhoneInterview,
} from '../types';

interface InlineInterviewPanelProps {
  applicant: Applicant;
  onClose: () => void;
  onSaved: () => void;
}

const RECOMMENDATION_OPTIONS: { value: InterviewRecommendation; label: string; color: string }[] = [
  { value: 'HIRE', label: 'Einstellen', color: '#22c55e' },
  { value: 'TRIAL', label: 'Probearbeiten', color: '#f59e0b' },
  { value: 'REJECT', label: 'Ablehnen', color: '#ef4444' },
  { value: 'UNDECIDED', label: 'Unentschieden', color: '#94a3b8' },
];

function formatDate(iso?: string): string {
  if (!iso) return '–';
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatEmploymentType(type?: string): string {
  const map: Record<string, string> = { MINIJOB: 'Minijob', WERKSTUDENT: 'Werkstudent', TEILZEIT: 'Teilzeit', VOLLZEIT: 'Vollzeit', KURZFRISTIG: 'Kurzfristig' };
  return type ? (map[type] || type) : '–';
}

function formatTeam(team?: string): string {
  const map: Record<string, string> = { SERVICE: 'Service', PRODUCTION: 'Produktion', MARKETING: 'Marketing' };
  return team ? (map[team] || team) : '–';
}

export default function InlineInterviewPanel({ applicant: initialApplicant, onClose, onSaved }: InlineInterviewPanelProps): React.JSX.Element {
  const [applicant, setApplicant] = useState<Applicant>(initialApplicant);
  const [formData, setFormData] = useState<InterviewFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [criteriaResponses, setCriteriaResponses] = useState<Record<string, any>>({});
  const [standardResponses, setStandardResponses] = useState<Record<string, any>>({});
  const [customResponses, setCustomResponses] = useState<Record<string, any>>({});
  const [meetsMinimum, setMeetsMinimum] = useState<boolean | null>(null);
  const [rating, setRating] = useState<number>(0);
  const [recommendation, setRecommendation] = useState<InterviewRecommendation | null>(null);
  const [notes, setNotes] = useState('');
  const [pastInterviews, setPastInterviews] = useState<PhoneInterview[]>([]);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const full = await RecruitingService.getApplicant(initialApplicant.id);
        setApplicant(full);
      } catch {
        // fallback to list data
      }
      loadForm();
      loadPastInterviews();
    };
    loadAll();
  }, [initialApplicant.id]);

  const loadForm = async () => {
    try {
      setLoading(true);
      const data = await InterviewService.getInterviewForm(applicant.id);
      setFormData(data);
    } catch {
      // template may not exist yet — form still works with standard questions
    } finally {
      setLoading(false);
    }
  };

  const loadPastInterviews = async () => {
    try {
      const interviews = await InterviewService.fetchInterviews(applicant.id);
      setPastInterviews(interviews);
    } catch {
      // silent
    }
  };

  const handleSubmit = async () => {
    if (!rating || rating < 1) {
      toast.error('Bitte Gesamtbewertung vergeben.');
      return;
    }
    if (!recommendation) {
      toast.error('Bitte Empfehlung auswählen.');
      return;
    }
    try {
      setSubmitting(true);
      const data: PhoneInterviewSubmitData = {
        criteria_responses: criteriaResponses,
        standard_responses: standardResponses,
        custom_responses: customResponses,
        meets_minimum_criteria: meetsMinimum ?? true,
        overall_rating: rating as 1 | 2 | 3 | 4 | 5,
        recommendation,
        notes: notes || undefined,
      };
      await InterviewService.submitInterview(applicant.id, data);
      toast.success('Interview gespeichert.');
      onSaved();
    } catch {
      toast.error('Interview konnte nicht gespeichert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 10px', borderRadius: 8,
    border: `1px solid ${colors.border}`, fontSize: 12, outline: 'none',
    boxSizing: 'border-box', background: '#fff',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 10, fontWeight: 600, color: colors.textMuted,
    marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.3px',
  };

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: colors.textMuted, fontSize: 12 }}>
        Lade Interviewleitfaden...
      </div>
    );
  }

  return (
    <div style={{ padding: '16px 20px 20px', background: 'rgba(82, 113, 165, 0.03)' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaPhoneAlt style={{ color: colors.slate, fontSize: 13 }} />
          <span style={{ fontSize: 14, fontWeight: 700, color: colors.navy }}>
            {'Telefoninterview — '}{applicant.first_name} {applicant.last_name}
          </span>
          {formData?.template && (
            <span style={{
              fontSize: 11, color: colors.slate, padding: '2px 10px',
              background: 'rgba(82, 113, 165, 0.08)', borderRadius: 6, fontWeight: 500,
            }}>
              {formData.template.name}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            display: 'flex', alignItems: 'center', gap: 4,
            padding: '4px 10px', borderRadius: 6,
            border: `1px solid ${colors.border}`, background: colors.bgCard,
            cursor: 'pointer', fontSize: 11, color: colors.textMuted,
          }}
        >
          <FaChevronUp style={{ fontSize: 9 }} /> {'Schließen'}
        </button>
      </div>

      {/* 2-column layout */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'start' }}>
        {/* LEFT: Compact CV */}
        <div style={{
          background: colors.bgCard, borderRadius: 12, border: `1px solid ${colors.border}`,
          padding: 14, fontSize: 12, display: 'flex', flexDirection: 'column', gap: 8,
          position: 'sticky', top: 0, maxHeight: 'calc(100vh - 280px)', overflowY: 'auto',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingBottom: 8, borderBottom: `1px solid ${colors.borderLight}` }}>
            <div style={{
              width: 36, height: 36, borderRadius: 18, flexShrink: 0,
              background: `linear-gradient(135deg, ${colors.bgMuted}, ${colors.border})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <FaUser size={14} color={colors.textLight} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: colors.navy }}>
                {applicant.first_name} {applicant.last_name}
              </div>
              <div style={{ fontSize: 10, color: colors.textMuted }}>
                {'Beworben: '}{formatDate(applicant.applied_at)}
                {applicant.birthday && <>{' | Geb.: '}{formatDate(applicant.birthday)}</>}
              </div>
            </div>
          </div>

          {/* Kontakt */}
          <CvRow icon={<FaEnvelope />} value={applicant.email} />
          <CvRow icon={<FaPhone />} value={applicant.phone || '–'} />
          {(applicant.street || applicant.city) && (
            <CvRow icon={<FaMapMarkerAlt />} value={
              [applicant.street, applicant.house_no].filter(Boolean).join(' ')
              + (applicant.zip_code || applicant.city ? ', ' : '')
              + [applicant.zip_code, applicant.city].filter(Boolean).join(' ')
            } />
          )}

          {/* CV/Resume Download */}
          {applicant.cv_file_url && (
            <a
              href={applicant.cv_file_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '7px 10px', borderRadius: 8,
                background: 'rgba(82, 113, 165, 0.06)', border: '1px solid rgba(82, 113, 165, 0.15)',
                textDecoration: 'none', color: colors.slate, fontSize: 11, fontWeight: 600,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(82, 113, 165, 0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(82, 113, 165, 0.06)'; }}
            >
              <FaFileAlt style={{ fontSize: 13 }} />
              <span style={{ flex: 1 }}>Lebenslauf / CV</span>
              <FaDownload style={{ fontSize: 10 }} />
            </a>
          )}

          {/* Beschäftigungswünsche */}
          <SectionLabel>{'Beschäftigungswünsche'}</SectionLabel>
          <CvRow icon={<FaBriefcase />} label="Team" value={formatTeam(applicant.team_choice)} />
          <CvRow icon={<FaBriefcase />} label="Typ" value={formatEmploymentType(applicant.desired_employment_type)} />
          <CvRow icon={<FaCalendarAlt />} label="Start" value={applicant.desired_start_date ? formatDate(applicant.desired_start_date) : '–'} />
          <CvRow icon={<FaBriefcase />} label="Bereich" value={applicant.desired_work_area || '–'} />
          <CvRow icon={<FaBriefcase />} label="Std/Wo" value={applicant.hours_can_work != null ? `${applicant.hours_can_work} h` : '–'} />
          <CvRow icon={<FaCalendarAlt />} label="Geb.datum" value={applicant.birthday ? formatDate(applicant.birthday) : '–'} />
          <CvRow icon={<FaIdCard />} label={'Über 18'} value={applicant.is_over_18 != null ? (applicant.is_over_18 ? 'Ja' : 'Nein') : '–'} />

          {/* Status & Bildung */}
          <SectionLabel>Status</SectionLabel>
          <CvRow icon={<FaBriefcase />} label="Status" value={applicant.current_status || '–'} />
          <CvRow icon={<FaBriefcase />} label="Beschäftigt" value={applicant.is_already_employed != null ? (applicant.is_already_employed ? 'Ja' : 'Nein') : '–'} />
          {applicant.study_program && <CvRow icon={<FaBriefcase />} label="Studium" value={applicant.study_program} />}
          {applicant.semester && <CvRow icon={<FaBriefcase />} label="Semester" value={applicant.semester} />}
          {applicant.has_bachelor != null && <CvRow icon={<FaIdCard />} label="Bachelor" value={applicant.has_bachelor ? 'Ja' : 'Nein'} />}

          {/* Qualifikationen */}
          <SectionLabel>Qualifikationen</SectionLabel>
          <CvRow icon={<FaLanguage />} label="Mutterspr." value={applicant.native_language || '–'} />
          <CvRow icon={<FaLanguage />} label="Deutsch" value={applicant.german_level || '–'} />
          <CvRow icon={<FaLanguage />} label="Englisch" value={applicant.english_level || '–'} />
          <CvRow icon={<FaUtensils />} label="Gastro" value={applicant.has_gastro_experience != null ? (applicant.has_gastro_experience ? `Ja (${applicant.gastro_experience_years || 0} J.)` : 'Nein') : '–'} />
          <CvRow icon={<FaIdCard />} label="Rote Karte" value={applicant.has_red_card != null ? (applicant.has_red_card ? 'Ja' : 'Nein') : '–'} />
          {applicant.has_drivers_license != null && <CvRow icon={<FaIdCard />} label="Führersch." value={applicant.has_drivers_license ? 'Ja' : 'Nein'} />}

          {/* Persönliches */}
          <SectionLabel>{'Persönliches'}</SectionLabel>
          <CvRow icon={<FaIceCream />} label="Lieblingseis" value={applicant.favorite_ice_cream || '–'} />

          {/* Anschreiben & Motivation */}
          {(applicant.cover_letter || applicant.motivation_text) && (
            <>
              <SectionLabel>Anschreiben & Motivation</SectionLabel>

              {applicant.cover_letter && (
                <TextBlock label="Anschreiben" text={applicant.cover_letter} />
              )}

              {applicant.motivation_text && (
                <TextBlock label="Motivation" text={applicant.motivation_text} />
              )}
            </>
          )}

          {/* Past interviews */}
          {pastInterviews.length > 0 && (
            <>
              <div style={{ height: 1, background: colors.borderLight }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, marginBottom: 4, textTransform: 'uppercase' }}>
                  {'Frühere Interviews'} ({pastInterviews.length})
                </div>
                {pastInterviews.map(iv => (
                  <div key={iv.id} style={{
                    padding: '6px 8px', borderRadius: 6, background: colors.bgMuted,
                    marginBottom: 4, fontSize: 11,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: colors.textBody, fontWeight: 500 }}>
                        {new Date(iv.conducted_at).toLocaleDateString('de-DE')}
                      </span>
                      <span style={{
                        padding: '1px 6px', borderRadius: 9999, fontSize: 9, fontWeight: 600,
                        background: RECOMMENDATION_OPTIONS.find(o => o.value === iv.recommendation)?.color || colors.textMuted,
                        color: 'white',
                      }}>
                        {RECOMMENDATION_OPTIONS.find(o => o.value === iv.recommendation)?.label || iv.recommendation}
                      </span>
                    </div>
                    <div style={{ color: colors.textMuted, fontSize: 10, marginTop: 2 }}>
                      {'★'.repeat(iv.overall_rating)}{'☆'.repeat(5 - iv.overall_rating)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* RIGHT: Interview Guide */}
        <div style={{
          background: colors.bgCard, borderRadius: 12, border: `1px solid ${colors.border}`,
          padding: 20,
        }}>
          {/* Criteria from template */}
          {formData?.template?.criteria && formData.template.criteria.length > 0 && (
            <FormSection title="Kriterien">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                {formData.template.criteria.map(criterion => (
                  <div key={criterion.key}>
                    <label style={labelStyle}>
                      {criterion.label}
                      {criterion.minimum != null && <span style={{ fontWeight: 400 }}>{' (Min: '}{criterion.minimum}{')'}</span>}
                      {criterion.required && <span style={{ color: colors.danger }}> *</span>}
                    </label>
                    {criterion.type === 'select' && criterion.options ? (
                      <select
                        value={criteriaResponses[criterion.key] || ''}
                        onChange={e => setCriteriaResponses(prev => ({ ...prev, [criterion.key]: e.target.value }))}
                        style={inputStyle}
                      >
                        <option value="">{'— Wählen —'}</option>
                        {criterion.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : criterion.type === 'number' ? (
                      <input type="number" value={criteriaResponses[criterion.key] ?? ''} onChange={e => setCriteriaResponses(prev => ({ ...prev, [criterion.key]: e.target.value ? Number(e.target.value) : '' }))} style={inputStyle} />
                    ) : (
                      <input type="text" value={criteriaResponses[criterion.key] || ''} onChange={e => setCriteriaResponses(prev => ({ ...prev, [criterion.key]: e.target.value }))} style={inputStyle} />
                    )}
                  </div>
                ))}
              </div>
            </FormSection>
          )}

          {/* Standard Questions */}
          <FormSection title="Standardfragen">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
              {[
                { key: 'weekday_availability', label: 'Verfügbarkeit Wochentage' },
                { key: 'weekend_availability', label: 'Verfügbarkeit Wochenende' },
                { key: 'available_hours', label: 'Von–bis verfügbar' },
                { key: 'planned_travel', label: 'Reisen geplant?' },
                { key: 'other_absences', label: 'Andere Abwesenheiten?' },
                { key: 'earliest_start', label: 'Frühester Start' },
              ].map(q => (
                <div key={q.key}>
                  <label style={labelStyle}>{q.label}</label>
                  <input
                    type="text"
                    value={standardResponses[q.key] || ''}
                    onChange={e => setStandardResponses(prev => ({ ...prev, [q.key]: e.target.value }))}
                    style={inputStyle}
                    placeholder="Antwort..."
                  />
                </div>
              ))}
            </div>
          </FormSection>

          {/* Custom questions from template */}
          {formData?.template?.custom_questions && formData.template.custom_questions.length > 0 && (
            <FormSection title="Zusatzfragen">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                {formData.template.custom_questions.map(q => (
                  <div key={q.key} style={q.type === 'textarea' ? { gridColumn: '1 / -1' } : undefined}>
                    <label style={labelStyle}>{q.label}</label>
                    {q.type === 'textarea' ? (
                      <textarea value={customResponses[q.key] || ''} onChange={e => setCustomResponses(prev => ({ ...prev, [q.key]: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                    ) : q.type === 'select' && q.options ? (
                      <select value={customResponses[q.key] || ''} onChange={e => setCustomResponses(prev => ({ ...prev, [q.key]: e.target.value }))} style={inputStyle}>
                        <option value="">{'— Wählen —'}</option>
                        {q.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : (
                      <input type="text" value={customResponses[q.key] || ''} onChange={e => setCustomResponses(prev => ({ ...prev, [q.key]: e.target.value }))} style={inputStyle} />
                    )}
                  </div>
                ))}
              </div>
            </FormSection>
          )}

          {/* Free Notes */}
          <FormSection title="Freie Notizen">
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Zusätzliche Beobachtungen, Eindrücke..."
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </FormSection>

          {/* Rating & Recommendation */}
          <div style={{
            display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap',
            padding: '14px 0 0', borderTop: `1px solid ${colors.borderLight}`,
          }}>
            <div>
              <label style={labelStyle}>{'Mindestkriterien erfüllt?'}</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[true, false].map(val => (
                  <button key={String(val)} onClick={() => setMeetsMinimum(val)} style={{
                    padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 500, cursor: 'pointer',
                    border: meetsMinimum === val ? 'none' : `1px solid ${colors.border}`,
                    background: meetsMinimum === val ? (val ? '#dcfce7' : '#fee2e2') : colors.bgCard,
                    color: meetsMinimum === val ? (val ? '#166534' : '#991b1b') : colors.textBody,
                  }}>
                    {val ? 'Ja' : 'Nein'}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Gesamtbewertung</label>
              <div style={{ display: 'flex', gap: 2 }}>
                {([1, 2, 3, 4, 5] as const).map(star => (
                  <button key={star} onClick={() => setRating(star)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 1, fontSize: 18 }}>
                    {star <= rating ? <FaStar color="#f59e0b" /> : <FaRegStar color={colors.border} />}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label style={labelStyle}>Empfehlung</label>
              <div style={{ display: 'flex', gap: 4 }}>
                {RECOMMENDATION_OPTIONS.map(opt => (
                  <button key={opt.value} onClick={() => setRecommendation(opt.value)} style={{
                    padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    border: recommendation === opt.value ? 'none' : `1px solid ${colors.border}`,
                    background: recommendation === opt.value ? opt.color : colors.bgCard,
                    color: recommendation === opt.value ? 'white' : colors.textBody,
                    transition: 'all 0.15s',
                  }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignSelf: 'flex-end' }}>
              <button onClick={onClose} style={{
                padding: '7px 14px', borderRadius: 8, border: `1px solid ${colors.border}`,
                background: colors.bgCard, cursor: 'pointer', fontSize: 12, fontWeight: 500,
              }}>
                Abbrechen
              </button>
              <button onClick={handleSubmit} disabled={submitting} style={{
                padding: '7px 14px', borderRadius: 8, border: 'none',
                background: 'linear-gradient(135deg, #5271A5, #3b5998)', color: 'white',
                cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
                opacity: submitting ? 0.7 : 1,
              }}>
                {submitting ? 'Speichern...' : 'Interview speichern'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---- Helpers ---- */

function CvRow({ icon, label, value }: { icon: React.ReactNode; label?: string; value: string }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
      <span style={{ color: colors.textLight, fontSize: 10, width: 14, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
      {label && <span style={{ color: colors.textMuted, minWidth: 65, fontSize: 10 }}>{label}</span>}
      <span style={{ color: colors.textBody, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
    </div>
  );
}

function TextBlock({ label, text }: { label: string; text: string }): React.JSX.Element {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, marginBottom: 2, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{
        fontSize: 11, color: colors.textBody, lineHeight: 1.5,
        whiteSpace: 'pre-wrap', maxHeight: 100, overflowY: 'auto',
        padding: '4px 0',
      }}>
        {text}
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{
      fontSize: 9, fontWeight: 700, color: colors.slate, textTransform: 'uppercase',
      letterSpacing: '0.5px', paddingTop: 6, marginTop: 2,
      borderTop: `1px solid ${colors.borderLight}`,
    }}>
      {children}
    </div>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ marginBottom: 16 }}>
      <h4 style={{
        margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: colors.navy,
        borderBottom: `1px solid ${colors.borderLight}`, paddingBottom: 5,
      }}>
        {title}
      </h4>
      {children}
    </div>
  );
}
